import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2, FiCalendar } from "react-icons/fi";
import { MdOutlineSettingsBackupRestore } from "react-icons/md";
import Modal from "../components/ui/Modal";
import {
  useCampaignEventsQuery,
  useAddCampaignEventsMutation,
  useUpdateCampaignEventsMutation,
  useDeleteCampaignEventsMutation,
  useRestoreCampaignEventsMutation,
} from "apis/campaignEvent";
import { FaPlus } from "react-icons/fa6";
import CampaignEventsEdit from "components/campaignEvents/Edit";
import Table from "components/ui/Table";

const CATEGORY_COLORS = {
  media: "bg-blue-100 text-blue-800",
  postProduction: "bg-purple-100 text-purple-800",
  print: "bg-yellow-100 text-yellow-800",
  inspection: "bg-green-100 text-green-800",
  meeting: "bg-orange-100 text-orange-800",
  milestone: "bg-red-100 text-red-800",
};

// Format duration to display hours and minutes
const formatDuration = (duration) => {
  if (!duration) return "-";
  const hours = Math.floor(duration);
  const minutes = Math.round((duration - hours) * 60);

  if (hours === 0) {
    return `${minutes} min`;
  } else if (minutes === 0) {
    return `${hours} hr`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

// Convert 24-hour format to 12-hour format for display
const convertTo12Hour = (time24h) => {
  if (!time24h) return "";
  const [hours, minutes] = time24h.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
};

const CampaignEvents = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("active");
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useCampaignEventsQuery({ showAll: true });

  const { mutate: createCampaignEvent, isLoading: isCreateLoading } =
    useAddCampaignEventsMutation();

  const { mutate: updateCampaignEvent } =
    useUpdateCampaignEventsMutation();

  const { mutate: deleteCampaignEvent, isLoading: isDeleteLoading } =
    useDeleteCampaignEventsMutation();

  const { mutate: restoreCampaignEvent, isLoading: isRestoreLoading } =
    useRestoreCampaignEventsMutation();

  const viewData = useMemo(
    () =>
      (data || []).filter((ce) =>
        viewMode === "active" ? !ce.isDeleted : ce.isDeleted,
      ),
    [data, viewMode],
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Event Name",
        cell: (row) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.name}</div>
          </div>
        ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "eventTypeId.category",
        header: "Category",
        cell: (row) => {
          const cat = row.eventTypeId?.category;
          if (!cat) return "-";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-800"
                }`}
            >
              {cat}
            </span>
          );
        },
        isFilter: true,
      },
      {
        accessorKey: "durationHours",
        header: "Duration",
        cell: (row) => (
          <span className="text-sm font-medium">
            {formatDuration(row.durationHours)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "quantity",
        header: "Qty",
        cell: (row) => (
          <span className="text-sm">{row.quantity ?? "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "size",
        header: "Size",
        cell: (row) => (
          <span className="text-sm">{row.size || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "eventTypeId.requiresContractor",
        header: "Contractor",
        cell: (row) => {
          const requires = row.eventTypeId?.requiresContractor;
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${requires
                  ? "bg-orange-100 text-orange-800"
                  : "bg-green-100 text-green-800"
                }`}
            >
              {requires ? "Required" : "Not Required"}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "eventTypeId.specificHours",
        header: "Available Times",
        cell: (row) => {
          const specificHours = row.eventTypeId?.specificHours;

          if (!specificHours || specificHours.length === 0) {
            return (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white border">
                Any time
              </span>
            );
          }

          return (
            <div className="flex flex-wrap gap-1 max-w-[200px]">
              {specificHours.map((time, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white border"
                >
                  {convertTo12Hour(time)}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
        cell: (row) =>
          viewMode === "active" ? (
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsEditOpen(true);
                }}
                className="edit-icon"
                title="Edit Campaign Event"
              >
                <FiEdit2 fontSize={20} />
              </button>
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete Campaign Event"
              >
                <FiTrash2 fontSize={20} />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsRestoreOpen(true);
                }}
                className="view-icon"
                title="Restore Campaign Event"
              >
                <MdOutlineSettingsBackupRestore fontSize={22} color="green" />
              </button>
            </div>
          ),
      },
    ],
    [viewMode]
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading campaign events: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedCampaignEvent = data?.find((event) => event._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateCampaignEvent({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createCampaignEvent(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteCampaignEvent(selectedId);
    setIsDeleteOpen(false);
  };

  const handleRestore = () => {
    restoreCampaignEvent(selectedId, {
      onSuccess: () => setIsRestoreOpen(false),
    });
  };

  const AddCampaignEventButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Campaign Event"
        className="max-w-4xl"
      >
        <CampaignEventsEdit
          initialData={selectedCampaignEvent}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Campaign Event"
        className="max-w-4xl"
      >
        <CampaignEventsEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Campaign Event"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedCampaignEvent?.name}"?
            It will be archived and can be restored later.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore Campaign Event"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to restore "{selectedCampaignEvent?.name}"?
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsRestoreOpen(false)}
              className="gray-button"
              disabled={isRestoreLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              className="black-button"
              disabled={isRestoreLoading}
            >
              Restore
            </button>
          </div>
        </div>
      </Modal>

      <div className="container mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiCalendar className="w-4 h-4" />
              <span>Active</span>
            </button>
            <button
              onClick={() => setViewMode("archive")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "archive"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiTrash2 className="w-4 h-4" />
              <span>Archive</span>
            </button>
          </div>
        </div>
        <Table
          title="Campaign Events"
          columns={columns}
          data={viewData}
          pageSize={pageSize}
          ButtonCmp={viewMode === "active" ? AddCampaignEventButton : undefined}
        />
      </div>
    </>
  );
};

export default CampaignEvents;
